'use client';

import { useHelpWizardStore } from '@/features/products/store/helpWizard';
import { clearFilters, useSolutionStore } from '@/features/products/store/solution';
import { stepValuesToFilters } from '@/features/products/utils/stepValuesToFilters';
import { useMediaQuery } from '@/hooks';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import CheckOutlined from '@mui/icons-material/CheckOutlined';
import { Dialog } from 'primereact/dialog';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { use100vh } from 'react-div-100vh';

import Step from '../Step';
import Stepper from '../Stepper';
import styles from './HelpDialog.module.scss';
import { getSteps } from './utils';

type HelpDialogProps = {
  handleClose: () => void;
};

const HelpDialog = ({ handleClose }: HelpDialogProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const filtersValue = useSolutionStore((s) => s.filtersValue);
  const steps = useHelpWizardStore((s) => s.steps);
  const { isMobile } = useMediaQuery();
  const height = use100vh();

  const getHeight = () => {
    if (!height) return '90vh';
    return isMobile ? height : height * 0.9;
  };

  useEffect(() => {
    if (filtersValue) {
      const stepList = getSteps(filtersValue);
      useHelpWizardStore.setState({ steps: stepList });
    }
  }, [filtersValue]);

  useEffect(() => {
    clearFilters();
  }, []);

  const clearValues = useCallback(() => {
    const stepArr = steps.map((x) => ({ ...x, values: [] }));
    useHelpWizardStore.setState({ steps: stepArr });
  }, [steps]);

  const filterProducts = useCallback(() => {
    const allValues = steps.flatMap((step) => step.values);
    useSolutionStore.setState({ filters: stepValuesToFilters(allValues) });
    clearValues();
    handleClose();
  }, [clearValues, handleClose, steps]);

  const handleGoBack = useCallback(() => {
    setActiveIndex((i) => i - 1);
  }, []);
  const handleNext = useCallback(() => {
    setActiveIndex((i) => i + 1);
  }, []);

  const renderFooter = useMemo(
    () => (
      <div className={styles.footer}>
        {activeIndex > 0 && (
          <Button startIcon={<ArrowBackOutlined />} onClick={handleGoBack} variant="outlined" autoFocus>
            Back
          </Button>
        )}
        {activeIndex < steps.length - 1 && (
          <Button
            disabled={!steps[activeIndex].values?.length}
            endIcon={<ArrowForwardOutlined />}
            onClick={handleNext}
            variant="outlined"
            autoFocus
          >
            Next
          </Button>
        )}
        {activeIndex === steps.length - 1 && (
          <Button
            disabled={!steps[activeIndex].values?.length}
            endIcon={<CheckOutlined />}
            onClick={filterProducts}
            variant="outlined"
            autoFocus
          >
            Done
          </Button>
        )}
      </div>
    ),
    [activeIndex, filterProducts, handleGoBack, handleNext, steps],
  );

  return (
    <Dialog
      header="Wizard"
      style={{ minHeight: getHeight() }}
      maximizable={!isMobile}
      visible
      className={styles.dialog}
      footer={renderFooter}
      onHide={handleClose}
    >
      <div className={styles.container}>
        <Stepper number={steps.length} activeIndex={activeIndex} />
        <Step index={activeIndex} />
      </div>
    </Dialog>
  );
};

export default HelpDialog;
